import * as React from "react";
import { renderToStaticMarkup } from "react-dom/server";
import { useGroupContext } from "groopit-js";
import { AssignmentDTO, ComponentType, GroopItModel_V1 } from "groopit-js";
import { ComponentDataDTO, IUserDTO } from "groopit-js/client";
import { ExpandTags } from "./NoteComponent";
import { useEffectOnce } from "react-use";
import { useCallback } from "react";
import { Tooltip } from "@material-ui/core";

interface Props {
  groopit: GroopItModel_V1;
  assignment: AssignmentDTO;
  children: React.ReactNode;
}

const componentToText = (
  component: ComponentDataDTO,
  pictureImageBase: string,
  users: IUserDTO[],
): string | undefined => {
  switch (component.model.type) {
    case ComponentType.Picture: {
      return pictureImageBase + component.image;
    }
    case ComponentType.PictureSet: {
      return component.pictures
        ?.map((p) => pictureImageBase + p.image)
        .join(", ");
    }
    case ComponentType.Location: {
      return component.location?.geography?.wellKnownText;
    }
    case ComponentType.Note: {
      return ExpandTags(component.notes, users);
    }
    case ComponentType.MultipleChoice: {
      return component.options;
    }
    case ComponentType.DateTime: {
      return component.model.dateOnly
        ? component.timestamp?.toLocaleDateString()
        : component.timestamp?.toLocaleString();
    }
    case ComponentType.MultipleChoiceExtended: {
      return JSON.parse(component.choice ?? "[]").join("\r\n");
    }
    case ComponentType.FixedDecimal: {
      return component.formattedNumber;
    }
    case ComponentType.SaveToSalesforce: {
      return component.displayName;
    }
    case ComponentType.InheritedData: {
      return component.displayValue;
    }
    case ComponentType.FollowUpRequest: {
      return component.model.description;
    }
    default:
      return `Unknown type ${component.model.type}`;
  }
};

export const groopitToPlainText = (
  groopit: GroopItModel_V1,
  pictureImageBase: string,
  users: IUserDTO[],
) => {
  let text =
    `Shared by ${
      groopit.creator.friendlyName
    } on ${groopit.timestamp.toLocaleString()}\n\n` +
    groopit.components
      .map(
        (c) =>
          `${c.model.description}\n${
            componentToText(c, pictureImageBase, users) ?? "unknown"
          }\n`,
      )
      .join("\n");
  if (groopit.salesforceLinks?.length ?? 0 > 0) {
    text += `Added to Salesforce: ${groopit.salesforceLinks
      .map((l) => l.name)
      .join(", ")}\n`;
  }
  return text;
};

export const emailInsight = (groopitModel: GroopItModel_V1) => {
  const { protocol, host } = window.location;
  return `If you have Groopit permissions for ${groopitModel.parentGroop.name} you can view the original post here: ${protocol}//${host}/Groopit/Details/${groopitModel.id}`;
};

export const groopitToHtml = (
  groopit: GroopItModel_V1,
  assignment: AssignmentDTO,
  baseUri: string,
  pictureImageBase: string,
  users: IUserDTO[],
) => {
  // When React uses renderToStaticMarkup it strips out the newline characters unless they're explicitly
  // in a <pre> tag. Rather than futz with trying to fix the monospace formatting, just replace the newlines
  // with <br/> tags.
  return renderToStaticMarkup(
    <>
      <hr />
      <div>
        <b>{assignment.title}</b>
      </div>
      <div>
        Shared by {groopit.creator.friendlyName} on{" "}
        {groopit.timestamp.toLocaleString()} via the Groopit Add-in for Outlook:
      </div>
      <br />
      <div>
        {groopit.components.map((c) => (
          <div key={c.model.componentModel_Id}>
            {c.model.description}:{" "}
            {componentToText(c, pictureImageBase, users)
              ?.trimEnd()
              ?.split(/\r*\n/g)
              .map((l, i) => (
                <React.Fragment key={i}>
                  {l}
                  <br />
                </React.Fragment>
              )) ?? "unknown"}
          </div>
        ))}
      </div>
      {groopit.salesforceLinks?.map((l) => (
        <div key={l.fieldName}>
          Added to Salesforce:{" "}
          {groopit.salesforceLinks.map((l) => l.name).join(", ")}
        </div>
      ))}
      <br />
      <hr />
      <div>
        The intel shown here was originally shared via email and has been
        captured for future decision-making via Groopit. To view all related
        insight, visit{" "}
        <a
          href={
            new URL(`/Groop/Groopits/${groopit.parentGroop.id}`, baseUri).href
          }
        >
          Groopit
        </a>
        .
      </div>
      <br />
      <br />
    </>,
  );
};

//
// Helper to generate the correct mailto: link for sending a groopit
// via email.
//
export const MailGroopit = (props: Props): JSX.Element => {
  const { groopit: groopitModel, assignment, children } = { ...props };
  const { users, pictureImageBase } = useGroupContext();

  return (
    <a
      href={`mailto:?subject=${encodeURIComponent(
        assignment.title ?? "",
      )}&body=${
        encodeURIComponent(
          groopitToPlainText(groopitModel, pictureImageBase, users),
        ) + encodeURIComponent("\r\n" + emailInsight(groopitModel))
      }`}
    >
      {children}
    </a>
  );
};

//
// MailGroopit builds a mailto: link for sharing a Groopit via email.
// Depending on your browser, OS, and email client there are different
// limitations on the size of that link. On the smaller end, it can be
// as low as 2000 characters which isn't hard to hit with some of our
// longer Groopits. CopyGroopit copies to the clipboard instead (the
// same format) so that the user can manually paste it into their email
// if they can't get MailGroopit to work.
//
export const CopyGroopit = (props: Props): JSX.Element => {
  const { groopit, assignment, children } = { ...props };
  const { users, pictureImageBase } = useGroupContext();
  const [tooltipTimer, setTooltipTimer] = React.useState<NodeJS.Timeout>();

  const copyToClipboard = useCallback(() => {
    navigator.clipboard.writeText(
      `${assignment.title ?? ""}\n${groopitToPlainText(
        groopit,
        pictureImageBase,
        users,
      )}`,
    );
    if (tooltipTimer) {
      clearTimeout(tooltipTimer);
    }
    setTooltipTimer(
      setTimeout(() => {
        setTooltipTimer(undefined);
      }, 3000),
    );
  }, [assignment.title, groopit, pictureImageBase, tooltipTimer, users]);

  useEffectOnce(() => {
    return () => {
      if (tooltipTimer) {
        clearTimeout(tooltipTimer);
        setTooltipTimer(undefined);
      }
    };
  });

  return (
    <Tooltip title="Insight copied to clipboard" open={Boolean(tooltipTimer)}>
      <button className="btn-link" onClick={copyToClipboard}>
        {children}
      </button>
    </Tooltip>
  );
};
